import type * as SentryType from '@sentry/react';
import type HttpError from '@shared/api-client/HttpError';
import type ResponseParseError from '@shared/api-client/ResponseParseError';

let sentryPromise: Promise<typeof SentryType> | null = null;

export function loadSentry() {
  sentryPromise = import('@sentry/react');
  return sentryPromise;
}

export async function captureException(
  ...args: Parameters<typeof SentryType.captureException>
) {
  const sentry = await sentryPromise;
  sentry?.captureException(...args);
}

export async function captureMessage(
  ...args: Parameters<typeof SentryType.captureMessage>
) {
  const sentry = await sentryPromise;
  sentry?.captureMessage(...args);
}

export async function withScope(callback: (scope: SentryType.Scope) => void) {
  const sentry = await sentryPromise;
  sentry?.withScope(callback);
}

export async function captureHttpError(error: HttpError) {
  const sentry = await sentryPromise;
  sentry?.captureException(error, {
    fingerprint: [normalizeIds(error.message)],
  });
}

export async function captureResponseParseError(error: ResponseParseError) {
  const sentry = await sentryPromise;
  sentry?.captureException(error, {
    fingerprint: [
      error.name,
      String(error.status),
      normalizeUrlPath(error.url),
    ],
    extra: {
      status: error.status,
      url: error.url,
    },
  });
}

function normalizeUrlPath(url: string) {
  try {
    return normalizeIds(new URL(url).pathname);
  } catch {
    return normalizeIds(url);
  }
}

export const normalizeIds = (input: string) => {
  const equipmentIdRegex = /\/[A-Z]{2,}(?:_[A-Za-z0-9-]+)+(?=\/|$)/g;
  const uuidRegex =
    /\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?=\/|$)/gi;
  const numericSegmentRegex = /\/\d+(?=\/|$)/g;
  return input
    .replace(equipmentIdRegex, '/{equipmentId}')
    .replace(uuidRegex, '/{uuid}')
    .replace(numericSegmentRegex, '/{id}')
    .trim();
};
